import * as React from "react"
import styled from "styled-components"
import { PostWidgetCard, PublicLocale, Widget } from "../../../types"
import Button from "../../Button/Button"
import { useTranslation } from "react-i18next"
import IconButton from "../../Button/IconButton"
import ModalFooterComponent from "../../Modals/Parts/ModalFooter"
import TooltipError from "../../Tooltips/ErrorTooltip"
import { ActiveStorageController, WidgetCardsController } from "../../../controllers"
import { closeCustomComponentModal } from "../../../store/modals/actions"
import { connect } from "react-redux"
import PowerSelect from "../../Form/PowerSelect"
import MobilityHelper from "../../../helpers/MobilityHelper"
import AvatarEditor from "../../Avatar/AvatarEditor"
import Loader from "../../Loaders/Loader"
import PageLoader from "../../Page/PageLoader"
import Dropzone from "../../Dropzone/Dropzone"
import { NEWS_COVER_IMAGE_FILE_SIZE, POST_COVER_IMAGE_CONTENT_TYPES, POST_COVER_IMAGE_FILE_SIZE } from "../../../Constants"
import { DropEvent, FileRejection } from "react-dropzone"

const Container = styled.div``

const ModalFooter = styled(ModalFooterComponent)`
  margin-left: -20px;
  margin-right: -20px;
  margin-bottom: -20px;
`

const DropzoneContainer = styled.div`
  width: 100%;
  max-height: 200px;
`

const CoverImageContainer = styled.div<{ modalMode?: boolean, embedded?: boolean }>`
  width: 100%;  
  overflow: hidden;
  margin: 0px;
  padding-top: 36.25%;
  position: relative;
`

const CoverImage = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  transform: translate(-50%, -50%);
  object-fit: cover;
`

interface IDispatchToProps {
  closeCustomComponentModal: typeof closeCustomComponentModal
}

type IProps = {
  widget: Widget
  card: PostWidgetCard
  onSubmit: (card: PostWidgetCard) => void
} & IDispatchToProps

interface IState {
  didInitialLoad: boolean
  selectedLocale: PublicLocale
  card: PostWidgetCard
  errors: any[]
}

const Post = (props: IProps) => {
  const { t } = useTranslation()
  const { widget } = props
  const [state, setState] = React.useState<IState>({
    didInitialLoad: false,
    selectedLocale: widget.locale,
    card: props.card,
    errors: []
  })
  const { errors, card, selectedLocale, didInitialLoad } = state

  React.useEffect(() => {
    setTimeout(() => {
      setState({ ...state, didInitialLoad: true })
    }, 500)
  }, [])

  const onErrorsDismiss = () => {
    setState({ ...state, errors: [] })
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    try {
      let response = null

      if (card.id) {
        response = await WidgetCardsController.update(widget.id, card)
      } else {
        response = await WidgetCardsController.create(widget.id, card)
      }

      if (!response.errors) {
        props.onSubmit(response)
        props.closeCustomComponentModal()
      } else {
        setState({ ...state, errors: response.errors })
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const onSelectedLocaleChange = () => {
    setState({ ...state, selectedLocale: selectedLocale })
  }

  const availableLocaleOptions = widget.supported_locales.map(locale => ({
    label: t(`PublicLocale::${locale}`,),
    value: locale
  }))
  const selectedLocaleOption = availableLocaleOptions.find(locale => locale.value === selectedLocale)

  const onCoverImageChange = (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
    try {
      const coverImage = acceptedFiles[0]

      ActiveStorageController.uploadPublic(coverImage, async (error, blob) => {
        if (error) { console.error(error) }
        if (blob) {
          const { url } = await ActiveStorageController.getBlobUrl(blob)

          setState({
            ...state,
            card: {
              ...card,
              translations: MobilityHelper.updateTranslation(selectedLocale, card.translations, 'cover_image_url', url)
            }
          })
        }
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  const onCoverImageDeleteClick = () => {
    const confirmed = confirm(t('Post::Are you sure you want to remove this cover image?'))

    if (confirmed) {
      setState({
        ...state,
        card: {
          ...card,
          translations: MobilityHelper.updateTranslation(selectedLocale, card.translations, 'cover_image_url', null)
        }
      })
    }
  }

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      card: {
        ...card,
        translations: MobilityHelper.updateTranslation(selectedLocale, card.translations, 'title', e.target.value)
      }
    })
  }

  const onSummaryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setState({
      ...state,
      card: {
        ...card,
        translations: MobilityHelper.updateTranslation(selectedLocale, card.translations, 'summary', e.target.value)
      }
    })
  }

  const onUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      card: {
        ...card,
        translations: MobilityHelper.updateTranslation(selectedLocale, card.translations, 'url', e.target.value)
      }
    })
  }

  if (!didInitialLoad) return <PageLoader />

  const coverImageUrl = MobilityHelper.getTranslation(selectedLocale, card.translations, 'cover_image_url') || ''

  return (
    <Container>
      <form onSubmit={onSubmit}>
        <div className="grid">
          <div className="grid-cell with-12col">
            <div className="form-item">
              <label>{t('Post::Locale')}</label>
              <PowerSelect
                options={availableLocaleOptions}
                value={selectedLocaleOption}
                onChange={onSelectedLocaleChange}
                placeholder={t('Widget::Select edit language')}
              />
            </div>
          </div>
          <div className="grid-cell with-12col">
            <div className="form-item">
              <label>{t('Post::Cover')}</label>
              {!coverImageUrl && <DropzoneContainer>
                <Dropzone
                  dropzoneProps={{
                    onDrop: onCoverImageChange,
                    multiple: false,
                    maxSize: POST_COVER_IMAGE_FILE_SIZE,
                    accept: POST_COVER_IMAGE_CONTENT_TYPES
                  }}
                />
              </DropzoneContainer>}
              {coverImageUrl && <CoverImageContainer onClick={onCoverImageDeleteClick}>
                <CoverImage src={coverImageUrl} />
              </CoverImageContainer>}
            </div>
          </div>
          <div className="grid-cell with-12col">
            <div className="form-item">
              <label>{t('Post::Title')}</label>
              <input
                type='text'
                value={MobilityHelper.getTranslation(selectedLocale, card.translations, 'title') || ''}
                placeholder={t('Post::Title')}
                onChange={onTitleChange}
              />
            </div>
          </div>
          <div className="grid-cell with-12col">
            <div className="form-item">
              <label>{t('Post::Summary')}</label>
              <textarea
                rows={4}
                value={MobilityHelper.getTranslation(selectedLocale, card.translations, 'summary') || ''}
                placeholder={t('Post::Summary')}
                onChange={onSummaryChange}
              />
            </div>
          </div>

          <div className="grid-cell with-12col">
            <div className="form-item">
              <label>{t('Post::Url')}</label>
              <input
                type='url'
                value={MobilityHelper.getTranslation(selectedLocale, card.translations, 'url') || ''}
                placeholder={t('Post::https://www.example.com')}
                onChange={onUrlChange}
              />
            </div>
          </div>
        </div>

        <ModalFooter>
          <div className='modal-footer'>
            <div />
            <div className='modal-footer-actions'>
              <div key='main-action' className='popover-wrapper'>
                <TooltipError
                  errors={errors}
                  onDismiss={onErrorsDismiss}
                />
                <Button
                  text={t('Post::Save')}
                  type='success'
                  onClick={onSubmit}
                />
              </div>
            </div>
          </div>
        </ModalFooter>

        <input type='submit' style={{ display: 'none' }} />
      </form>
    </Container>
  )
}

const mapDispatchToProps: IDispatchToProps = {
  closeCustomComponentModal
}

export default connect(null, mapDispatchToProps)(Post)